import { Inject, Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { IRaceDetails } from '../timing-tower/IRaceTiming';
import { FeatureSettingsService } from './feature-settings.service';
import { SERVER_ENDPOINT } from './websocket.token';

enum WEBSOCKET_DATA_TYPE {
  MOCK = 'mock',
  TEST = 'test',
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private eventSource: EventSource | null = null;
  private readonly dataSubject = new BehaviorSubject<IRaceDetails>({} as IRaceDetails);
  private readonly featureSettingsService = inject(FeatureSettingsService);

  public isConnected = signal(false); // Signal to track connection status

  public data$ = this.dataSubject.asObservable();

  constructor(@Inject(SERVER_ENDPOINT) private serverEndpoint: string) {
    this.initializeSSE();
  }

  private initializeSSE() {
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('ldatatype');

    const sseUrl = `${this.serverEndpoint}events`;

    this.eventSource = new EventSource(sseUrl, {
      withCredentials: true,
    });

    this.eventSource.onopen = () => {
      console.log('SSE connection opened');
      this.isConnected.set(true); // Update signal to true
    };

    let event: string;
    if (type === WEBSOCKET_DATA_TYPE.MOCK) {
      event = 'data-update-mock';
    } else if (type === WEBSOCKET_DATA_TYPE.TEST) {
      event = 'data-update-test';
    } else {
      event = 'data-update';
    }

    this.eventSource.addEventListener(event, (event) => {
      try {
        const data = JSON.parse(event.data);
        this.dataSubject.next(data);
      } catch (error) {
        console.error('Error parsing data-update event:', error);
      }
    });

    this.eventSource.addEventListener('app-features-update', (event) => {
      try {
        const data = JSON.parse(event.data);
        this.featureSettingsService.updateFeatureSettings(data);
        console.warn('Received app-features-update:', data);
      } catch (error) {
        console.error('Error parsing data-update event:', error);
      }
    });

    this.eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      this.isConnected.set(false); // Update signal to true

      // Attempt to reconnect after a delay
      if (this.eventSource?.readyState === EventSource.CLOSED) {
        console.log('SSE connection closed, attempting to reconnect in 5 seconds...');
        setTimeout(() => {
          this.initializeSSE();
        }, 5000);
      }
    };
  }

  public disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected.set(false); // Update signal to false
    }
  }
}
